export function createToolboxesPanel({
  mountEl,
  toolboxes = [],
  topbarId = "main-topbar",
  gapPx = 12,
  onToolClick = null,
  onToolboxTabClick = null,
} = {}) {
  if (!mountEl) return null;

  const normalizedToolboxes = (Array.isArray(toolboxes) ? toolboxes : []).filter(
    (tb) => Array.isArray(tb.tools) && tb.tools.length > 0
  );

  const state = {
    activeId: null,
    toolboxes: normalizedToolboxes,
  };

  let imageObserver = null;
  let progressiveRenderToken = 0;

  const RENDER_CHUNK_SIZE = 12;

  function el(tag, className = "", attrs = {}) {
    const node = document.createElement(tag);
    if (className) node.className = className;

    Object.entries(attrs).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      node.setAttribute(k, String(v));
    });

    return node;
  }

  function applyTopOffset() {
    const topbar = document.getElementById(topbarId);
    const topOffset = topbar ? topbar.offsetHeight + gapPx : gapPx;
    mountEl.style.top = `${topOffset}px`;
  }

  function applyMountStyle() {
    mountEl.classList.add("pano-toolboxes");
  }

  function getActiveToolbox() {
    return state.toolboxes.find((tb) => tb.id === state.activeId) || null;
  }

  function destroyImageObserver() {
    if (imageObserver) {
      imageObserver.disconnect();
      imageObserver = null;
    }
  }

  function ensureImageObserver(rootEl) {
    if (imageObserver) return imageObserver;

    imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const img = entry.target;
          const src = img.dataset.src;

          if (src && !img.src) {
            img.src = src;
          }

          imageObserver.unobserve(img);
        });
      },
      {
        root: rootEl || null,
        rootMargin: "120px",
        threshold: 0.01,
      }
    );

    return imageObserver;
  }

  function observeLazyImage(img, rootEl) {
    if (!img) return;
    ensureImageObserver(rootEl).observe(img);
  }

  function buildToolTile(t, viewportEl) {
    const isDraggable = !!t.is_draggable;
    const isClickable = !!t.is_clickable;

    const tile = el("button", "pano-toolbox__tool", {
      type: "button",
      title: t.name || "",
      "data-tool-id": t.id,
      "data-draggable": isDraggable ? "true" : "false",
      "data-clickable": isClickable ? "true" : "false",
      draggable: "false",
      "aria-label": t.name || "Tool",
    });

    if (isDraggable) tile.classList.add("is-draggable");
    else if (isClickable) tile.classList.add("is-clickable");
    else tile.classList.add("is-disabled");

    if (isClickable) {
      tile.addEventListener("click", () => {
        if (typeof onToolClick === "function") onToolClick(t);
      });
    }

    if (t.image_url) {
      const img = el("img", "pano-toolbox__tool-image");
      img.alt = t.name || "";
      img.loading = "lazy";
      img.decoding = "async";
      img.draggable = false;
      img.dataset.src = t.thumb_url || t.image_url;

      tile.appendChild(img);
      observeLazyImage(img, viewportEl);
    } else {
      const ph = el("div", "pano-toolbox__tool-placeholder");
      ph.textContent = t.name || "Tool";
      tile.appendChild(ph);
    }

    return tile;
  }

  function updateNavState(viewport, prevBtn, nextBtn) {
    if (!viewport || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const current = viewport.scrollLeft;

    const canPrev = current > 4;
    const canNext = current < maxScrollLeft - 4;

    prevBtn.style.opacity = canPrev ? "1" : ".35";
    nextBtn.style.opacity = canNext ? "1" : ".35";
    prevBtn.disabled = !canPrev;
    nextBtn.disabled = !canNext;
  }

  function buildTabs() {
    const tabs = el("div", "pano-toolboxes__tabs");

    state.toolboxes.forEach((tb) => {
      const tab = el("button", "pano-toolbox__tab", { type: "button" });
      tab.textContent = tb.name || `Toolbox #${tb.id}`;

      if (tb.id === state.activeId) tab.classList.add("is-active");

      tab.addEventListener("click", () => {
        if (typeof onToolboxTabClick === "function") {
          const result = onToolboxTabClick(tb);
          if (result === false) return;
        }

        if (state.activeId === tb.id) {
          state.activeId = null;
        } else {
          state.activeId = tb.id;
        }
        render();
      });

      tabs.appendChild(tab);
    });

    return tabs;
  }

  function progressiveAppendTools(
    grid,
    tools = [],
    viewportEl = null,
    prevBtn = null,
    nextBtn = null
  ) {
    progressiveRenderToken += 1;
    const token = progressiveRenderToken;

    let index = 0;

    function appendChunk() {
      if (token !== progressiveRenderToken) return;

      const frag = document.createDocumentFragment();
      const end = Math.min(index + RENDER_CHUNK_SIZE, tools.length);

      for (let i = index; i < end; i += 1) {
        frag.appendChild(buildToolTile(tools[i], viewportEl));
      }

      grid.appendChild(frag);
      index = end;

      updateNavState(viewportEl, prevBtn, nextBtn);

      if (index < tools.length) {
        requestAnimationFrame(appendChunk);
      } else {
        requestAnimationFrame(() => {
          updateNavState(viewportEl, prevBtn, nextBtn);
        });
      }
    }

    requestAnimationFrame(appendChunk);
  }

  function buildPanel() {
    const panel = el("div", "pano-toolboxes__panel");

    const active = getActiveToolbox();

    const prev = el("button", "pano-toolboxes__nav pano-toolboxes__nav--prev", {
      type: "button",
      "aria-label": "Scorri a sinistra",
    });
    prev.textContent = "‹";

    const next = el("button", "pano-toolboxes__nav pano-toolboxes__nav--next", {
      type: "button",
      "aria-label": "Scorri a destra",
    });
    next.textContent = "›";

    const viewport = el("div", "pano-toolboxes__viewport");
    const grid = el("div", "pano-toolboxes__grid");

    if (!active || !(active.tools || []).length) {
      const empty = el("div", "pano-toolboxes__empty");
      empty.textContent = "Nessun tool disponibile.";
      viewport.appendChild(empty);
    } else {
      viewport.appendChild(grid);
      progressiveAppendTools(grid, active.tools || [], viewport, prev, next);
    }

    prev.addEventListener("click", (e) => {
      e.stopPropagation();
      viewport.scrollBy({ left: -320, behavior: "smooth" });
    });

    next.addEventListener("click", (e) => {
      e.stopPropagation();
      viewport.scrollBy({ left: 320, behavior: "smooth" });
    });

    viewport.addEventListener("scroll", () => {
      updateNavState(viewport, prev, next);
    });

    panel.appendChild(prev);
    panel.appendChild(viewport);
    panel.appendChild(next);

    requestAnimationFrame(() => updateNavState(viewport, prev, next));

    return panel;
  }

  function buildDashboardButton() {
    const dashBtn = document.createElement("button");
    dashBtn.className = "pano-toolboxes__dashboard-btn";
    dashBtn.textContent = "Dashboard";
    dashBtn.type = "button";

    dashBtn.addEventListener("click", () => {
      window.location.href = "/";
    });

    return dashBtn;
  }

  function render() {
    progressiveRenderToken += 1;
    destroyImageObserver();

    mountEl.innerHTML = "";
    applyMountStyle();
    mountEl.classList.remove("hidden");

    const dashBtn = buildDashboardButton();
    mountEl.appendChild(dashBtn);

    if (!state.toolboxes.length) {
      mountEl.classList.add("pano-toolboxes--dashboard-only");
      return;
    }

    mountEl.classList.remove("pano-toolboxes--dashboard-only");
    mountEl.appendChild(buildTabs());

    if (state.activeId != null) {
      mountEl.appendChild(buildPanel());
    }
  }

  function setToolboxes(nextToolboxes) {
    state.toolboxes = Array.isArray(nextToolboxes) ? nextToolboxes : [];

    if (!state.toolboxes.some((tb) => tb.id === state.activeId)) {
      state.activeId = null;
    }

    render();
    applyTopOffset();
  }

  applyTopOffset();
  applyMountStyle();
  window.addEventListener("resize", applyTopOffset);
  render();

  return {
    setToolboxes,
    destroy() {
      progressiveRenderToken += 1;
      destroyImageObserver();
      window.removeEventListener("resize", applyTopOffset);
      mountEl.innerHTML = "";
      mountEl.style.top = "";
      mountEl.classList.remove("pano-toolboxes");
      mountEl.classList.remove("pano-toolboxes--dashboard-only");
    },
  };
}